import React, { useState, useRef, useEffect } from 'react';
import styles from './ChatBot.module.css';
import emailjs from '@emailjs/browser';

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [currentFlow, setCurrentFlow] = useState(null); // 'faq', 'quote', 'contact'
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
    });
    const [currentField, setCurrentField] = useState(null);
    
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // EmailJS configuration (same as your contact form)
    const EMAILJS_CONFIG = {
        SERVICE_ID: 'service_jeyrucd',
        TEMPLATE_ID: 'Contact_Form',
        PUBLIC_KEY: 'JrcxVFzAMPjwVqLB0'
    };

    // FAQs data
    const faqs = {
        'services': {
        question: 'What services do you offer?',
        answer: 'We offer custom software development, web applications, mobile apps, API integration, and digital transformation solutions.'
        },
        'pricing': {
        question: 'How much do your services cost?',
        answer: 'We provide custom quotes based on project requirements. Would you like me to help you get a quote?'
        },
        'timeline': {
        question: 'How long does a typical project take?',
        answer: 'Project timelines vary based on complexity. Small projects: 2-4 weeks, Medium: 1-3 months, Large: 3-6+ months.'
        },
        'technologies': {
        question: 'What technologies do you work with?',
        answer: 'We work with React, Node.js, Python, .NET, mobile frameworks, cloud platforms (AWS, Azure), and modern databases.'
        },
        'support': {
        question: 'Do you provide ongoing support?',
        answer: 'Yes! We offer maintenance plans, technical support, and feature updates for all our projects.'
        }
    };

    // Scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 300);
        }
    }, [isOpen, currentFlow]);

    // Initialize chat with greeting
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            addBotMessage("Hello! I'm Apexbit's assistant. How can I help you today? You can ask about our services, request a quote, or contact our team.");
        }
    }, [isOpen]);

    const addBotMessage = (text, delay = 1000) => {
        setIsTyping(true);
        setTimeout(() => {
            setMessages(prev => [...prev, { text, isUser: false, timestamp: new Date() }]);
            setIsTyping(false);
        }, delay);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { text, isUser: true, timestamp: new Date() }]);
    };

    const handleSend = async () => {
        if (!inputValue.trim()) return;

        const userMessage = inputValue.trim().toLowerCase();
        addUserMessage(inputValue);
        setInputValue('');

        // Handle different conversation flows
        if (currentFlow === 'quote' || currentFlow === 'contact') {
            handleFormFlow(userMessage);
            return;
        }

        // Check for FAQ matches
        const faqMatch = Object.entries(faqs).find(([key, faq]) => 
            userMessage.includes(key) || 
            userMessage.includes(faq.question.toLowerCase())
        );

        if (faqMatch) {
            const [key, faq] = faqMatch;
            addBotMessage(faq.answer);
            
            if (key === 'pricing') {
                setTimeout(() => {
                    addBotMessage("Would you like me to help you get a specific quote for your project?");
                }, 1500);
            }
            return;
        }

        // Handle intent detection
        if (userMessage.includes('quote') || userMessage.includes('price') || userMessage.includes('cost')) {
            startQuoteFlow();
        } else if (userMessage.includes('contact') || userMessage.includes('talk to') || userMessage.includes('human')) {
            startContactFlow();
        } else if (userMessage.includes('service') || userMessage.includes('what do you do')) {
            addBotMessage(faqs.services.answer);
        } else {
            // If no specific intent, offer options
            addBotMessage("I'm not sure I understand. You can:");
            setTimeout(() => {
                addBotMessage("1. Ask about our services\n2. Request a quote\n3. Contact our team directly\n\nWhat would you like to do?");
            }, 500);
        }
    };

    const startQuoteFlow = () => {
        setCurrentFlow('quote');
        setFormData(prev => ({ ...prev, subject: 'Quote Request' }));
        addBotMessage("I'd be happy to help you get a quote! Let me collect some information.");
        setTimeout(() => {
            askForField('name');
        }, 1000);
    };

    const startContactFlow = () => {
        setCurrentFlow('contact');
        addBotMessage("I'll help you contact our team. They'll get back to you within 24 hours.");
        setTimeout(() => {
            askForField('name');
        }, 1000);
    };

    const askForField = (field) => {
        const fieldQuestions = {
            name: "What's your full name?",
            email: "What's your email address?",
            phone: "What's your phone number? (optional)",
            company: "What company do you represent? (optional)",
            subject: "What's the subject of your message?",
            message: "Please describe your project or inquiry:"
        };

        if (currentFlow === 'quote' && field === 'subject') {
            // Skip subject for quote requests, it will be auto-generated
            askForField('message');
            return;
        }

        setCurrentField(field);
        addBotMessage(fieldQuestions[field]);
    };

    const handleFormFlow = (userMessage) => {
        if (!currentField) return;

        // Update form data
        setFormData(prev => ({
            ...prev,
            [currentField]: userMessage
        }));

        // Determine next field
        const fieldOrder = ['name', 'email', 'phone', 'company', 'subject', 'message'];
        const currentIndex = fieldOrder.indexOf(currentField);
        const nextField = fieldOrder[currentIndex + 1];

        if (nextField) {
            setTimeout(() => {
                askForField(nextField);
            }, 500);
        } else {
            // All fields collected
            setTimeout(() => {
                submitForm();
            }, 500);
        }
    };

    const submitForm = async () => {
        setIsTyping(true);
        
        try {
            // For quote requests, auto-generate subject
            const finalFormData = {
                ...formData,
                subject: currentFlow === 'quote' ? `Quote Request By ${formData.name}` : formData.subject
            };

            await emailjs.send(
                EMAILJS_CONFIG.SERVICE_ID,
                EMAILJS_CONFIG.TEMPLATE_ID,
                {
                    from_name: finalFormData.name,
                    from_email: finalFormData.email,
                    from_phone: finalFormData.phone,
                    company: finalFormData.company,
                    subject: finalFormData.subject,
                    message: finalFormData.message,
                    to_name: 'Apexbit Team',
                    reply_to: finalFormData.email
                },
                EMAILJS_CONFIG.PUBLIC_KEY
            );

            addBotMessage(`Thank you ${formData.name}! Your ${currentFlow === 'quote' ? 'quote request' : 'message'} has been sent to our team. We'll contact you within 24 hours.`);
        
        } catch (error) {
            console.error('Failed to send message:', error);
            addBotMessage("I'm sorry, there was an error sending your message. Please contact us directly at hello@apexbit.com");
        }

        // Reset flow
        setCurrentFlow(null);
        setCurrentField(null);
        setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            subject: '',
            message: ''
        });
        setIsTyping(false);
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setMessages([]);
            setCurrentFlow(null);
            setCurrentField(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
        {/* Chat Button */}
        <button 
            className={`${styles.chatButton} ${isOpen ? styles.hidden : ''}`}
            onClick={toggleChat}
            aria-label="Open chat"
        >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
            </svg>
        </button>

        {/* Chat Window */}
        {isOpen && (
            <div className={styles.chatWindow}>
            {/* Header */}
            <div className={styles.chatHeader}>
                <div className={styles.botInfo}>
                    <div className={styles.botAvatar}>A</div>
                    <div>
                        <h3>Apexbit Assistant</h3>
                        <span className={styles.status}>Online</span>
                    </div>
                </div>
                <button 
                className={styles.closeButton}
                onClick={toggleChat}
                aria-label="Close chat"
                >
                ×
                </button>
            </div>

            {/* Messages */}
            <div className={styles.messagesContainer}>
                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`${styles.message} ${message.isUser ? styles.userMessage : styles.botMessage}`}
                    >
                        {!message.isUser && <div className={styles.botAvatarSmall}>A</div>}
                        <div className={styles.messageBubble}>
                            {message.text.split('\n').map((line, i) => (
                                <span key={i}>{line}<br /></span>
                            ))}
                        </div>
                    </div>
                ))}
                
                {isTyping && (
                <div className={styles.message}>
                    <div className={styles.botAvatarSmall}>A</div>
                    <div className={styles.typingIndicator}>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={styles.inputContainer}>
                <div className={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className={styles.textInput}
                    disabled={isTyping}
                />
                <button
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isTyping}
                    className={styles.sendButton}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                </button>
                </div>
            </div>
            </div>
        )}
        </>
    );
};

export default ChatBot;